/**
 * ProfitWell Pixel Manager
 * Handles ProfitWell initialization after email collection and consent
 */

import type { ConsentManager, ProfitWellOptions } from '../types/analytics.types';
import type { StatsManager } from '../core/stats-manager';
import type { UserRepository } from '../data/user-repository';
import type { EventName, EventData, EventMetadata } from '../types/events.types';

/**
 * Dependencies for ProfitWellPixelManager
 */
interface ProfitWellPixelDeps {
  consentManager: ConsentManager;
  statsManager: StatsManager;
  userRepository?: UserRepository;
}

// ProfitWell public auth token (same value previously in the index.html data-pw-auth attribute).
const PROFITWELL_AUTH = 'b841cd37b2746c59c2d1694507236548';

/**
 * ProfitWell Pixel Manager
 */
export class ProfitWellPixelManager {
  private isStarted = false;
  private userEmail: string | null = null;
  private deps: Partial<ProfitWellPixelDeps> = {};

  /**
   * Initialize ProfitWell with dependencies
   */
  init(deps: Partial<ProfitWellPixelDeps>): void {
    // Validate required dependencies
    if (!deps.consentManager || !deps.statsManager) {
      throw new Error(
        'ProfitWellPixelManager.init() requires consentManager and statsManager dependencies'
      );
    }

    this.deps = deps;

    console.log('ProfitWell Pixel Manager initialized (waiting for consent)');

    // Subscribe to StatsManager events ONLY after consent is granted
    this.deps.consentManager!.onConsentGranted(() => {
      // Inject the real ProfitWell script only now (full consent) — never pre-consent.
      this.loadScript();
      // Subscribe after consent granted
      this.deps.statsManager!.subscribe(this.handleStatsManagerEvent.bind(this));
      console.log('ProfitWell subscribed to StatsManager events after consent');

      // Check if we already have email from injected UserRepository (optional dep)
      if (this.deps.userRepository && this.deps.userRepository.get('email')) {
        this.startTracking(this.deps.userRepository.get('email') as string);
      }
    });
  }

  /**
   * Inject the ProfitWell script. Called only after FULL consent (never pre-consent).
   * The window.profitwell queue shim (index.html) buffers any start() call until it loads.
   */
  private loadScript(): void {
    if (document.getElementById('profitwell-js')) {
      return;
    }
    const script = document.createElement('script');
    script.id = 'profitwell-js';
    script.async = true;
    script.setAttribute('data-pw-auth', PROFITWELL_AUTH);
    script.src = `https://public.profitwell.com/js/profitwell.js?auth=${PROFITWELL_AUTH}`;
    document.head.appendChild(script);
  }

  /**
   * Handle events from StatsManager
   */
  private handleStatsManagerEvent(
    eventName: EventName,
    eventData: EventData,
    _eventMetadata: EventMetadata
  ): void {
    // ProfitWell only needs email capture to start tracking
    if (eventName === 'email_captured' && eventData.email) {
      this.startTracking(eventData.email as string);
    }
    // Optionally track subscription completion
    else if (eventName === 'subscription_completed') {
      // ProfitWell JS API doesn't support revenue tracking directly
      // It relies on server-side integration or webhook data
      console.log('ProfitWell: Subscription completed event received', eventData);
    }
  }

  /**
   * Start tracking with user email
   * Called after email is collected and consent is obtained
   */
  startTracking(email: string): void {
    if (!email) {
      console.warn('ProfitWell: Cannot start tracking without email');
      return;
    }

    // Check for consent
    if (this.deps.consentManager && !this.deps.consentManager.hasConsent()) {
      console.log('ProfitWell: Waiting for analytics consent');
      return;
    }

    if (this.isStarted && this.userEmail === email) {
      console.log('ProfitWell: Already tracking this user');
      return;
    }

    // Start ProfitWell tracking with only supported parameter
    if (window.profitwell) {
      const options: ProfitWellOptions = { user_email: email };
      window.profitwell('start', options);
      this.isStarted = true;
      this.userEmail = email;
      console.log('ProfitWell tracking started for:', email);
    } else {
      console.warn('ProfitWell script not loaded');
    }
  }

  /**
   * Update user email if changed
   */
  updateEmail(newEmail: string): void {
    if (!newEmail) return;

    // Check for consent
    if (this.deps.consentManager && !this.deps.consentManager.hasConsent()) {
      console.log('ProfitWell: Waiting for analytics consent');
      return;
    }

    if (!this.isStarted) {
      // If not started yet, start with new email
      this.startTracking(newEmail);
      return;
    }

    if (this.userEmail === newEmail) {
      return; // No change needed
    }

    // Update the user with new email only (ProfitWell JS only supports user_email)
    if (window.profitwell) {
      const options: ProfitWellOptions = { user_email: newEmail };
      window.profitwell('start', options);
      this.userEmail = newEmail;
      console.log('ProfitWell email updated to:', newEmail);
    }
  }
}

// Create singleton instance
export const profitWellPixel = new ProfitWellPixelManager();
